import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { 
 Users, GraduationCap, Clock, Plus, Search, 
 Trash2, Edit2, CheckCircle, CreditCard,
 Wallet, PieChart, Package, ShieldAlert, UserPlus,
 Printer, LayoutDashboard, Zap, Activity, FileStack, Settings,
 Upload, Camera, FlipHorizontal, X, ChevronDown, Calendar,
 Image as ImageIcon
} from 'lucide-react';
import StudentsManagement from '../../components/admin/StudentsManagement';
import TeachersManagement from '../../components/admin/TeachersManagement';
import ExamsManagement from '../../components/admin/ExamsManagement';
import ApprovalsManagement from '../../components/admin/ApprovalsManagement';
import GalleryManagement from '../../components/admin/GalleryManagement';
import SchoolBranding from '../../components/admin/SchoolBranding';

import { useGetDashboardStats, useListApprovals } from '../../hooks/useQueries';

// --- ANIMATION VARIANTS ---
const containerVar = {
 visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
 hidden: { opacity: 0 }
};

const itemVar = {
 visible: { y: 0, opacity: 1 },
 hidden: { y: 20, opacity: 0 }
};

// --- HELPER COMPONENTS ---
const ActionCard = ({ icon: Icon, label, color, onClick }: any) => {
 const themes = {
  blue: 'border-blue-100 bg-blue-50/30 text-blue-600 hover:bg-blue-600 hover:text-white',
  purple: 'border-purple-100 bg-purple-50/30 text-purple-600 hover:bg-purple-600 hover:text-white',
  orange: 'border-orange-100 bg-orange-50/30 text-orange-600 hover:bg-orange-600 hover:text-white',
  amber: 'border-amber-100 bg-amber-50/30 text-amber-600 hover:bg-amber-600 hover:text-white',
  red: 'border-rose-100 bg-rose-50/30 text-rose-600 hover:bg-rose-600 hover:text-white',
  indigo: 'border-indigo-100 bg-indigo-50/30 text-indigo-600 hover:bg-indigo-600 hover:text-white',
  emerald: 'border-emerald-100 bg-emerald-50/30 text-emerald-600 hover:bg-emerald-600 hover:text-white',
 };

 return (
  <button 
   onClick={onClick} 
   className={`flex flex-col items-center justify-center gap-2 p-5 bg-white border rounded-[5px] shadow-sm transition-all duration-300 group hover:shadow-2xl active:scale-95 tracking-widest hover:-translate-y-1 ${themes[color as keyof typeof themes]}`}
  >
    <div className="p-3 rounded-[5px] transition-all duration-300">
     <Icon size={20} className="group-hover:scale-110 transition-transform" />
    </div>
    <span className="text-[10px] font-black tracking-[0.2em] text-center leading-tight">{label}</span>
  </button>
 );
};

const StatCard = ({ icon: Icon, title, value, color, subText }: any) => {
 const colorStyles = {
  blue: 'text-blue-600 bg-blue-50/50',
  amber: 'text-amber-600 bg-amber-50/50',
  emerald: 'text-emerald-600 bg-emerald-50/50',
 };

 return (
  <motion.div variants={itemVar} className="premium-card p-6 md:p-8 bg-white border border-slate-100 shadow-sm hover:shadow-2xl active:scale-95 tracking-widest transition-all">
   <div className="flex justify-between items-start">
    <div>
     <p className="text-slate-400 text-[10px] font-black tracking-widest mb-4 italic uppercase">{title}</p>
     <div className="flex items-baseline gap-2">
      <h3 className="text-4xl font-black text-slate-900 tracking-tighter leading-none uppercase">{value}</h3>
      {subText && <span className="text-[10px] text-slate-400 font-bold tracking-tight">{subText}</span>}
     </div>
    </div>
    <div className={`p-4 rounded-[5px] ${colorStyles[color as keyof typeof colorStyles]}`}>
     <Icon size={24} className="opacity-80" />
    </div>
   </div>
   <div className="mt-6 pt-6 border-t border-slate-50 flex items-center justify-between">
     <div className="flex items-center gap-2">
      <div className={`w-2 h-2 rounded-full ${color === 'blue' ? 'bg-blue-500 animate-pulse' : color === 'amber' ? 'bg-amber-500' : 'bg-emerald-500'}`}></div>
       <span className="text-[9px] font-black text-slate-400 tracking-wider">Connected</span>
     </div>
     <Activity size={14} className="text-slate-200" />
   </div>
  </motion.div>
 );
};

const AdminDashboard = () => {
 const navigate = useNavigate();
 const location = useLocation();
 const queryParams = new URLSearchParams(location.search);
 const initialTab = queryParams.get('tab') || 'overview';

 const [activeTab, setActiveTab] = useState(initialTab);
 const [currentTime, setCurrentTime] = useState(new Date());

 // ✅ React Query Hooks for Persistence & Offline Support
 const { data: stats, isLoading: statsLoading } = useGetDashboardStats();
 const { data: pendingStudents, isLoading: pendingLoading } = useListApprovals();

 useEffect(() => { 
  const timer = setInterval(() => setCurrentTime(new Date()), 1000);
  return () => clearInterval(timer);
 }, []);

 useEffect(() => {
  const tab = new URLSearchParams(location.search).get('tab');
  if (tab) setActiveTab(tab);
 }, [location.search]);

 const handleAction = async (action: string, table: string, idValue: any) => {
  if (action === 'delete' && !window.confirm("Confirm deletion?")) return;
  let err;
  const pkColumn = table === 'students' ? 'student_id' : 'id';
  
  if (action === 'delete') ({ error: err } = await supabase.from(table).delete().eq(pkColumn, idValue));
  if (action === 'approve') ({ error: err } = await supabase.from('students').update({ is_approved: 'approved' }).eq('student_id', idValue));
  
  if (!err) { 
    toast.success("Done!"); 
    // Data will auto-invalidate if we use mutations, but for now manual refetch
    window.location.reload(); 
  } 
  else { toast.error(err.message); }
 };

  if (statsLoading || pendingLoading) return <div className="h-screen flex items-center justify-center font-black text-slate-400  text-xs uppercase tracking-widest">LOADING...</div>;

 const counts = stats || { students: 0, teachers: 0, pending: 0, admins: 0 };

 return (
  <motion.div initial="hidden" animate="visible" variants={containerVar} className="min-h-screen bg-[#F8FAFC] p-1 md:p-2 pb-32">
   <div className="max-w-full mx-auto space-y-10">
    
    {/* --- HEADER & TOP ACTIONS --- */}
    <motion.div variants={itemVar} className="bg-white border border-slate-100 rounded-[5px] p-8 md:p-10 shadow-sm transition-all hover:shadow-md">
     <div className="flex flex-col lg:flex-row justify-between items-center gap-8 mb-10">
      <div className="flex items-center gap-4 text-center lg:text-left">
        <div className="w-14 h-14 bg-blue-600 rounded-[5px] flex items-center justify-center text-white shadow-2xl active:scale-95 tracking-widest shadow-blue-200 animate-float">
         <LayoutDashboard size={28}/>
        </div>
        <div>
         <h1 className="text-3xl font-black text-slate-900  leading-none uppercase">Admin Dashboard</h1>
         <p className="text-[10px] font-black text-blue-500 tracking-widest mt-2">School Management</p>
        </div>
      </div>
      <div className="flex flex-wrap justify-center gap-3">
        <button onClick={() => navigate('/admin/create-exam')} className="px-6 py-3 bg-slate-900 text-white rounded-[5px] text-[10px] font-black tracking-widest hover:bg-black transition-all shadow-lg flex items-center gap-2">
         <Zap size={14} className="text-yellow-400"/> Exams
        </button>
        <button onClick={() => navigate('/admin/manage-fees')} className="px-6 py-3 bg-blue-50 text-blue-600 rounded-[5px] text-[10px] font-black tracking-widest hover:bg-blue-600 hover:text-white transition-all border border-blue-100 shadow-sm flex items-center gap-2">
         <CreditCard size={14}/> Fees
        </button>
        <button onClick={() => navigate('/admin/upload-result')} className="px-6 py-3 bg-emerald-50 text-emerald-600 rounded-[5px] text-[10px] font-black tracking-widest hover:bg-emerald-600 hover:text-white transition-all border border-emerald-100 shadow-sm flex items-center gap-2">
         <CheckCircle size={14}/> Results
        </button>
      </div>
     </div>

     <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
       <ActionCard icon={Wallet} label="Accounting" color="blue" onClick={() => navigate('/admin/manage-salaries')} />
       <ActionCard icon={FileStack} label="Docs Hub" color="orange" onClick={() => navigate('/admin/documents')} />
       <ActionCard icon={PieChart} label="Staff Payroll" color="purple" onClick={() => navigate('/admin/teacher-salary')} />
        <ActionCard icon={Package} label="Inventory" color="amber" onClick={() => navigate('/admin/inventory')} />
        <ActionCard icon={ShieldAlert} label="Security" color="red" onClick={() => navigate('/admin/create-admin')} />
        <ActionCard icon={UserPlus} label="Add Student" color="indigo" onClick={() => navigate('/admin/add-student')} />
        <ActionCard icon={Plus} label="Add Teacher" color="emerald" onClick={() => navigate('/admin/add-teacher')} />
       <ActionCard icon={ImageIcon} label="Gallery" color="purple" onClick={() => setActiveTab('gallery')} />
       <ActionCard icon={Settings} label="Branding" color="blue" onClick={() => setActiveTab('branding')} />
     </div>
    </motion.div>

    {/* --- STATS --- */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
     <StatCard icon={GraduationCap} title="Total Students" value={counts.students} color="blue" subText="Enrolled students" />
     <StatCard icon={Clock} title="Pending Approvals" value={counts.pending} color="amber" subText="Action required" />
     <StatCard icon={Users} title="Teachers" value={counts.teachers} color="emerald" subText="School staff" />
    </div>

    {/* --- TABLES SECTION --- */}
    <motion.div variants={itemVar} className="bg-white border border-slate-100 rounded-[5px] overflow-hidden shadow-sm mt-10">
     <div className="flex flex-wrap border-b border-slate-100 p-2 gap-2 bg-slate-50/50">
      {['overview', 'students', 'teachers', 'admins', 'exams', 'approvals', 'gallery', 'branding'].map(tab => (
       <button 
        key={tab} 
        onClick={() => setActiveTab(tab)} 
        className={`px-8 py-3.5 text-[10px] font-black tracking-widest rounded-[5px] transition-all duration-300 ${
         activeTab === tab 
          ? 'bg-blue-600 text-white shadow-lg shadow-blue-100' 
          : 'text-slate-400 hover:text-slate-600 hover:bg-white'
        }`}
       >
        {tab}
       </button>
      ))}
     </div>

     <div className="p-4 md:p-8">
      <AnimatePresence mode="wait">
       {activeTab === 'overview' && (
        <motion.div key="ov" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {pendingStudents.map((s: any) => (
           <div key={s.student_id} className="p-6 rounded-[5px] border border-slate-100 bg-slate-50/30 flex flex-col justify-between h-48 group hover:border-blue-200 transition-all">
            <div>
             <h4 className="font-black text-slate-800 text-lg leading-tight tracking-tight">{s.full_name}</h4>
             <span className="inline-block mt-2 bg-blue-50 text-blue-600 px-3 py-1 rounded-[5px] text-[9px] font-black tracking-wider">Class {s.class_name}</span>
            </div>
            <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
              <button onClick={() => handleAction('approve', 'students', s.student_id)} className="flex-1 bg-blue-600 text-white py-3 rounded-[5px] text-[9px] font-black tracking-widest hover:bg-blue-700 shadow-md transition-all">Approve</button>
              <button onClick={() => handleAction('delete', 'students', s.student_id)} className="flex-1 bg-white text-rose-500 py-3 rounded-[5px] text-[9px] font-black tracking-widest border border-rose-100 hover:bg-rose-50 transition-all">Reject</button>
            </div>
           </div>
          ))}
          {pendingStudents.length === 0 && <div className="col-span-full py-20 text-center opacity-30 font-black  text-[10px] text-slate-400">No pending registration requests</div>}
        </motion.div>
       )}

       {activeTab === 'students' && (
        <motion.div key="st" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
         <StudentsManagement />
        </motion.div>
       )}

        {activeTab === 'teachers' && (
         <motion.div key="tch" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
          <TeachersManagement roleFilter="teacher" />
         </motion.div>
        )}
 
        {activeTab === 'admins' && (
         <motion.div key="adm" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
          <TeachersManagement roleFilter="admin" />
         </motion.div>
        )}

       {activeTab === 'exams' && (
        <motion.div key="exm" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
         <ExamsManagement />
        </motion.div>
       )}

       {activeTab === 'approvals' && (
        <motion.div key="app" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
         <ApprovalsManagement />
        </motion.div>
       )}

        {activeTab === 'gallery' && (
         <motion.div key="gal" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
          <GalleryManagement />
         </motion.div>
        )}

        {activeTab === 'branding' && (
         <motion.div key="brand" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
          <SchoolBranding />
         </motion.div>
        )}
      </AnimatePresence>
     </div>
    </motion.div>
   </div>
  </motion.div>
 );
};

export default AdminDashboard;
